import { CheckCircle2, MessageSquareText } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCompleteEmailSignup } from "@/api/hooks";
import { detectIOS } from "@/lib/isIOS";
import { TERMS_VERSION } from "@/routes/legal/TermsPage";

/** Lands when a brand-new user clicks the "Set up your account"
 *  email — the one ``/auth/magic-link/request`` sends when the
 *  address has no User yet.
 *
 *  Two paths from here:
 *
 *  - **Open in PencilOut app** (iOS Mail tap → Safari → here):
 *    fires ``pencilout://auth/claim-signup?token=...`` so the
 *    iOS app can present the form natively. The user typed
 *    their email on the iPhone; finishing on the iPhone is the
 *    natural place. Only renders the chooser when the request
 *    came from iOS Safari, just like MagicLinkConsumePage.
 *
 *  - **Set up on web** (default for non-iOS, or when the user
 *    explicitly picks it on iOS): inline form for name + company
 *    + terms. Submit → ``/auth/email/complete-signup`` →
 *    drops the session cookie + navigates to ``/``.
 *
 *  Mirror of the phone-path inline-signup that the OTP sheet
 *  handles after no_account from ``/auth/phone/verify``. Keeps
 *  the email path UX-symmetric: same number of steps, no
 *  bouncing between Sign In / Sign Up screens.
 */
export function ClaimSignupPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const fromIOS = searchParams.get("from") === "ios";
  const navigate = useNavigate();
  const isIOS = detectIOS();
  const complete = useCompleteEmailSignup();

  const [chose, setChose] = useState<"app" | "web" | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [agreed, setAgreed] = useState(false);

  // Show the chooser only when:
  //   - we have a token to forward
  //   - the user is on iOS Safari
  //   - the URL doesn't already say from=ios (which means they
  //     came from inside the iOS app via a different flow and
  //     should stay on web for this completion)
  //   - they haven't picked yet
  const showChooser = Boolean(token) && isIOS && !fromIOS && chose !== "web";

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    complete.mutate(
      {
        verified_email_token: token,
        display_name: displayName.trim(),
        company_name: companyName.trim(),
        terms_accepted_version: TERMS_VERSION,
      },
      {
        onSuccess: () => {
          // Cookie + bearer set server-side; navigate to dashboard.
          navigate("/", { replace: true });
        },
      },
    );
  }

  // Wrapper renders branding + a card body that swaps in based
  // on state.
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md text-center">
        <div className="mb-6">
          <span className="brand-wordmark text-3xl">PencilOut</span>
        </div>

        {!token && <MissingTokenCard />}

        {token && showChooser && <IOSChooser token={token} onPickWeb={() => setChose("web")} />}

        {token && !showChooser && (
          <form
            onSubmit={onSubmit}
            className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6 text-left"
          >
            <div>
              <h1 className="font-mono text-lg font-bold tracking-tight">
                Set up your PencilOut account
              </h1>
              <p className="mt-2 text-sm text-graphite-soft">
                Almost there. Tell us a bit about your business so we can finish provisioning your
                workspace.
              </p>
            </div>

            <label className="block">
              <span className="label-mono">Your name</span>
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Smith"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="label-mono">Company name</span>
              <input
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Smith Remodeling"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            <label className="flex items-start gap-2 text-xs text-graphite-soft">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                required
                className="mt-0.5"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" className="underline hover:text-graphite">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline hover:text-graphite">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {complete.error && (
              <p className="text-sm text-red-pencil">
                {complete.error instanceof ApiError
                  ? complete.error.detail
                  : "Could not finish signup."}
              </p>
            )}

            <button
              type="submit"
              disabled={complete.isPending || !displayName.trim() || !companyName.trim() || !agreed}
              className="w-full rounded bg-graphite px-3 py-2.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
            >
              {complete.isPending ? "Setting up…" : "Create account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function MissingTokenCard() {
  return (
    <div className="space-y-3 rounded-lg border border-rule bg-paper-warm p-6 text-left">
      <h1 className="font-mono text-lg font-bold tracking-tight">Missing signup token</h1>
      <p className="text-sm text-graphite-soft">
        This link is incomplete — the token query parameter is gone. Head back to the sign-in
        screen, enter your email, and request a fresh link.
      </p>
      <Link
        to="/login"
        className="inline-block rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper hover:bg-graphite-soft"
      >
        Back to sign-in →
      </Link>
    </div>
  );
}

function IOSChooser({
  token,
  onPickWeb,
}: {
  token: string;
  onPickWeb: () => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-rule bg-paper-warm p-6 text-left">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-graphite" aria-hidden />
        <div>
          <h1 className="font-mono text-lg font-bold tracking-tight">Open in PencilOut?</h1>
          <p className="mt-2 text-sm text-graphite-soft">
            Looks like you're on iPhone or iPad. Finish setting up your account in the PencilOut app
            — same form, native keyboard, signed-in immediately. Or tap the second button to set up
            here on the web.
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2 pt-2">
        <a
          href={`pencilout://auth/claim-signup?token=${encodeURIComponent(token)}`}
          className="block rounded bg-graphite px-3 py-2 text-center font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
        >
          Open in PencilOut app
        </a>
        <button
          type="button"
          onClick={onPickWeb}
          className="rounded border border-rule bg-paper px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
        >
          Set up here on web instead
        </button>
      </div>
      <p className="font-mono text-[10px] text-graphite-faint">
        No PencilOut app yet? Tap "Set up on web" — you can install the iOS app later.
      </p>
      {/* Keep the icon import used so eslint doesn't complain — and
          give the chooser a subtle visual anchor. */}
      <div className="flex items-center gap-1 pt-1 text-[10px] text-graphite-faint">
        <MessageSquareText className="h-3 w-3" aria-hidden /> Token good for 15 min
      </div>
    </div>
  );
}
